import type {
  EventConfig,
  EventExtraConfig,
  EventSummary,
  InventoryItem,
  TicketExtraPurchase,
  TicketUser,
  ValidationResponse,
  UserTicketSummary,
  AdminGuestlistResponse,
  AdminPersonRecord,
  AdminOrderRecord,
  AdminInventorySkuRecord,
  AdminEventTicketSummary,
  PersonSummary,
  InviteRecord,
  BulkPersonalInviteResult,
  PersonalInviteExportRow,
  HouseQuestionnaireAnswers,
  HouseQuestionnaireSubmission,
  QuestionDefinition,
  TicketTier
} from './types';
import { getConvex } from './convexClient';

export function mapEventSummary(event: any): EventSummary {
  return {
    id: event.id,
    name: event.name,
    schedule: event.schedule,
    location: event.location,
    whatsappGroupUrl: event.whatsappGroupUrl ?? null,
    applicationUrl: event.applicationUrl ?? null,
    ticketPrice: event.ticketPrice ?? null,
    currency: event.currency ?? event.ticket?.currency
  };
}

export function mapEventConfig(eventDoc: any): EventConfig {
  const extras: EventExtraConfig[] = (eventDoc.extras ?? []).map((extra: any) => ({
    sku: extra.sku,
    name: extra.name,
    description: extra.description ?? undefined,
    price: extra.price,
    currency: extra.currency,
    capacity: extra.capacity,
    maxPerOrder: extra.maxPerOrder,
    isUnlimited: typeof extra.capacity === 'number' && extra.capacity < 0
  }));

  return {
    id: eventDoc.eventId,
    name: eventDoc.name,
    headline: eventDoc.headline,
    description: eventDoc.description ?? null,
    flyerUrl: eventDoc.flyerUrl ?? null,
    whatsappGroupUrl: eventDoc.whatsappGroupUrl ?? null,
    applicationUrl: eventDoc.applicationUrl ?? null,
    location: eventDoc.location,
    schedule: eventDoc.schedule,
    ticket: {
      productName: eventDoc.ticket.productName,
      currency: eventDoc.ticket.currency,
      startPrice: eventDoc.ticket.startPrice,
      endPrice: eventDoc.ticket.endPrice,
      dynamicPricing: eventDoc.ticket.dynamicPricing ?? undefined,
      allowInvites: eventDoc.ticket.allowInvites ?? undefined,
      defaultInviteAllowance: eventDoc.ticket.defaultInviteAllowance ?? undefined,
      preCheckoutQuestion: eventDoc.ticket.preCheckoutQuestion ?? null
    },
    extras
  };
}

export function mapInventoryItem(item: any): InventoryItem {
  const capacity = typeof item.capacity === 'number' ? item.capacity : 0;
  const isUnlimited = Boolean(item.isUnlimited) || capacity < 0;
  const available = typeof item.available === 'number' ? item.available : 0;
  return {
    sku: item.sku,
    name: item.name,
    description: item.description ?? undefined,
    price: item.price,
    currency: item.currency,
    capacity,
    sold: item.sold,
    reserved: item.reserved,
    available: isUnlimited ? Number.MAX_SAFE_INTEGER : available,
    maxPerOrder: item.maxPerOrder ?? 1,
    isUnlimited
  };
}

export function mapTicketExtras(extras: any[]): TicketExtraPurchase[] {
  return extras.map(extra => ({
    sku: extra.sku,
    quantity: extra.quantity,
    purchasedAt: extra.purchasedAt ?? null,
    unitPrice: extra.unitPrice ?? null,
    totalPrice: extra.totalPrice ?? null,
    currency: extra.currency ?? null,
    reservationId: extra.reservationId ?? null,
    metadata: extra.metadata ?? null
  }));
}

export function mapInviteRecord(doc: any): InviteRecord {
  return {
    id: typeof doc._id === 'string' ? doc._id : String(doc._id),
    code: doc.code,
    eventId: doc.eventId,
    kind: doc.kind,
    inviterTicketId: doc.inviterTicketId ? String(doc.inviterTicketId) : null,
    inviterPersonId: doc.inviterPersonId ? String(doc.inviterPersonId) : null,
    targetTicketId: doc.targetTicketId ? String(doc.targetTicketId) : null,
    targetPersonId: doc.targetPersonId ? String(doc.targetPersonId) : null,
    targetNormalizedEmail: doc.targetNormalizedEmail ?? null,
    maxUses: doc.maxUses,
    usedCount: doc.usedCount,
    status: doc.status,
    issuedAt: doc.issuedAt ?? 0
  } satisfies InviteRecord;
}

export function mapPersonSummary(person: any, fallbackId?: string): PersonSummary {
  if (!person) {
    return {
      id: fallbackId ?? '',
      name: 'Guest',
      email: null,
      phone: null,
      externalId: null,
      metadata: null
    };
  }

  const id =
    typeof person.id === 'string'
      ? person.id
      : typeof person._id === 'string'
      ? person._id
      : fallbackId ?? '';

  return {
    id,
    name: person.name ?? 'Guest',
    email: person.email ?? null,
    phone: person.phone ?? null,
    externalId: person.externalId ?? null,
    metadata: person.metadata ?? null
  };
}

export function mapTicketUser(ticket: any): TicketUser {
  const fallbackPersonId = ticket.personId ? String(ticket.personId) : undefined;
  const metadata =
    ticket.metadata && typeof ticket.metadata === 'object' && !Array.isArray(ticket.metadata)
      ? ticket.metadata
      : null;
  const preCheckoutAnswer = (() => {
    if (!metadata) return null;
    const value = metadata.preCheckoutAnswer;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  })();
  return {
    id: ticket.code,
    eventId: ticket.eventId,
    redeemed: ticket.status === 'redeemed',
    redeemedAt: ticket.redeemedAt ?? null,
    person: mapPersonSummary(ticket.person, fallbackPersonId ?? undefined),
    extras: mapTicketExtras(ticket.extras ?? []),
    origin: ticket.origin ?? undefined,
    status: ticket.status,
    preCheckoutAnswer
  };
}

export async function fetchConvexEventSummaries(): Promise<EventSummary[]> {
  try {
    const convex = getConvex() as any;
    const events = await convex.query('events:list', {});
    if (!Array.isArray(events)) {
      return [];
    }
    return events.map(mapEventSummary);
  } catch (error) {
    console.error('Failed to load Convex events', error);
    return [];
  }
}

export async function fetchConvexTicketValidation(eventId: string, code: string): Promise<ValidationResponse | null> {
  try {
    const convex = getConvex() as any;
    const result = await convex.query('tickets:detail', { eventId, code });
    if (!result || !result.ticket || !result.event) {
      return null;
    }

    return {
      user: mapTicketUser(result.ticket),
      event: mapEventConfig(result.event),
      inventory: (result.inventory ?? []).map(mapInventoryItem),
      community: result.community ?? null,
      tiers: result.tiers ?? [],
      eventCapacity: result.eventCapacity ?? null,
      ticketPrice:
        result.ticketPrice ??
        result.event?.ticket?.startPrice ??
        0
    };
  } catch (error) {
    console.error('Failed to load Convex ticket detail', error);
    throw error instanceof Error ? error : new Error('Failed to load ticket detail');
  }
}

export async function fetchConvexEventDetail(eventId: string): Promise<{
  event: EventConfig;
  inventory: InventoryItem[];
  ticketPrice: number;
} | null> {
  try {
    const convex = getConvex() as any;
    const result = await convex.query('events:detail', { eventId });
    if (!result || !result.event) {
      return null;
    }
    return {
      event: mapEventConfig(result.event),
      inventory: (result.inventory ?? []).map(mapInventoryItem),
      ticketPrice: result.ticketPrice ?? result.event.ticket?.startPrice ?? 0
    };
  } catch (error) {
    console.error('Failed to load Convex event detail', error);
    return null;
  }
}

export async function releaseConvexReservation(reservationId: string, reason?: string): Promise<boolean> {
  try {
    const convex = getConvex() as any;
    await convex.mutation('reservations:forceRelease', {
      reservationId,
      reason: reason ?? undefined
    });
    return true;
  } catch (error) {
    console.error('Failed to release Convex reservation', error);
    return false;
  }
}

export async function sweepExpiredReservations(eventId: string | null): Promise<{ released: number; skipped: number }> {
  if (!eventId) {
    return { released: 0, skipped: 0 };
  }

  try {
    const convex = getConvex() as any;
    const result = await convex.mutation('reservations:sweepExpired', {
      eventId,
      reason: 'expired_admin_cleanup'
    });
    const released = Number(result?.released ?? 0);
    const skipped = Number(result?.skipped ?? 0);
    return { released, skipped };
  } catch (error) {
    console.warn('Expired reservation sweep failed', error);
    return { released: 0, skipped: 0 };
  }
}

export async function addAdminTicketExtra(
  ticketId: string,
  payload: { sku: string; quantity: number; unitPrice?: number; currency?: string; metadata?: unknown }
): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('admin:addTicketExtra', {
    ticketId,
    sku: payload.sku,
    quantity: payload.quantity,
    unitPrice: payload.unitPrice ?? undefined,
    currency: payload.currency ?? undefined,
    metadata: payload.metadata ?? undefined
  });
}

export async function removeAdminTicketExtra(
  ticketExtraId: string,
  options: { quantity?: number; reason?: string } = {}
): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('admin:removeTicketExtra', {
    ticketExtraId,
    quantity: options.quantity ?? undefined,
    reason: options.reason ?? undefined
  });
}

export async function updateAdminTicketExtraQuantity(
  ticketExtraId: string,
  quantity: number,
  reason?: string
): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('admin:updateTicketExtraQuantity', {
    ticketExtraId,
    quantity,
    reason: reason ?? undefined
  });
}

export interface CheckoutExtrasInput {
  sku: string;
  quantity: number;
}

export interface CheckoutSessionResponse {
  sessionId: string;
  reservationId: string | null;
}

export async function createStripeCheckoutSession(params: {
  eventId: string;
  // Absent => the checkout action mints the issued row itself (single purchase
  // entry, two-lists slice 05). Present => extras-only / invited flow.
  ticketCode?: string;
  donationAmount: number;
  extras: CheckoutExtrasInput[];
  allowExtrasOnly?: boolean;
  tierId?: string;
  // Invited buyer (two-lists slice 06): claims the invite at purchase.
  inviteCode?: string;
  // Custom experience return path (allowlisted server-side, e.g. '/vidamor').
  returnPath?: string;
}): Promise<CheckoutSessionResponse> {
  const convex = getConvex() as any;
  return convex.action('stripe:createCheckoutSession', params);
}

export interface AdminEventConfig {
  id: string;
  community?: { slug: string; name: string; visibility: string } | null;
  name: string;
  headline: string;
  description?: string | null;
  flyerUrl?: string | null;
  whatsappGroupUrl?: string | null;
  applicationUrl?: string | null;
  location: {
    name: string;
    address: string | null;
    mapUrl: string | null;
  };
  schedule: {
    start: string;
    end: string;
  };
  ticket: {
    productName: string;
    currency: string;
    startPrice: number;
    endPrice: number;
    dynamicPricing?: {
      start: string | null;
      end: string | null;
    } | null;
    allowInvites?: boolean;
    defaultInviteAllowance?: number | null;
    preCheckoutQuestion?: string | null;
  };
  extras: Array<{
    sku: string;
    name: string;
    description: string | null;
    price: number;
    currency: string;
    capacity: number;
    maxPerOrder: number;
  }>;
}

/** AdminEventConfig plus the community-layer config the manage area edits. */
export interface ManagedEventConfig extends AdminEventConfig {
  overrides: {
    visibilityOverride: string;
    ticketAccess: string;
    applicationMode: string;
  };
  ticketing: {
    capacity: number | null;
    tiers: Array<{
      id: string;
      name: string;
      description: string | null;
      capacity: number;
      price: number;
      dynamic: { endPrice: number; start: string | null; end: string | null } | null;
      active?: boolean;
      salesStart?: string | null;
      salesEnd?: string | null;
    }>;
  };
}

export function mapManagedEventConfig(doc: any): ManagedEventConfig {
  return {
    ...mapAdminEventConfig(doc),
    overrides: {
      visibilityOverride: doc.overrides?.visibilityOverride ?? 'inherit',
      ticketAccess: doc.overrides?.ticketAccess ?? 'invite',
      applicationMode: doc.overrides?.applicationMode ?? 'inherit'
    },
    ticketing: {
      capacity: doc.ticketing?.capacity ?? null,
      tiers: (doc.ticketing?.tiers ?? []).map((tier: any) => ({
        id: tier.id,
        name: tier.name,
        description: tier.description ?? null,
        capacity: tier.capacity,
        price: tier.price,
        dynamic: tier.dynamic ?? null,
        active: tier.active !== false,
        salesStart: tier.salesStart ?? null,
        salesEnd: tier.salesEnd ?? null
      }))
    }
  };
}

export function mapAdminEventConfig(doc: any): AdminEventConfig {
  return {
    id: doc.id,
    community: doc.community ?? null,
    name: doc.name,
    headline: doc.headline,
    description: doc.description ?? null,
    flyerUrl: doc.flyerUrl ?? null,
    whatsappGroupUrl: doc.whatsappGroupUrl ?? null,
    applicationUrl: doc.applicationUrl ?? null,
    location: doc.location,
    schedule: doc.schedule,
    ticket: {
      productName: doc.ticket.productName,
      currency: doc.ticket.currency,
      startPrice: doc.ticket.startPrice,
      endPrice: doc.ticket.endPrice,
      dynamicPricing: doc.ticket.dynamicPricing ?? null,
      allowInvites: doc.ticket.allowInvites ?? undefined,
      defaultInviteAllowance: doc.ticket.defaultInviteAllowance ?? undefined,
      preCheckoutQuestion: doc.ticket.preCheckoutQuestion ?? null
    },
    extras: (doc.extras ?? []).map((extra: any) => ({
      sku: extra.sku,
      name: extra.name,
      description: extra.description ?? null,
      price: extra.price,
      currency: extra.currency,
      capacity: extra.capacity,
      maxPerOrder: extra.maxPerOrder
    }))
  };
}

export async function fetchAdminEvents(): Promise<AdminEventConfig[]> {
  try {
    const convex = getConvex() as any;
    const configs = await convex.query('events:listConfigs', {});
    if (!Array.isArray(configs)) {
      return [];
    }
    return configs.map(mapAdminEventConfig);
  } catch (error) {
    console.error('Failed to load Convex events configs', error);
    return [];
  }
}

export async function upsertAdminEvent(event: AdminEventConfig, communityId?: string): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('events:upsert', {
    event: {
      // communityId only matters on create: it binds the new event to the
      // community whose managers are creating it. The server ignores it on
      // updates.
      ...(communityId ? { communityId } : {}),
      id: event.id,
      name: event.name,
      headline: event.headline,
      flyerUrl: event.flyerUrl ?? null,
      whatsappGroupUrl: event.whatsappGroupUrl ?? null,
      applicationUrl: event.applicationUrl ?? null,
      location: event.location,
      description: event.description ?? null,
      schedule: event.schedule,
      ticket: {
        productName: event.ticket.productName,
        currency: event.ticket.currency,
        startPrice: event.ticket.startPrice,
        endPrice: event.ticket.endPrice,
        dynamicPricing: event.ticket.dynamicPricing ?? undefined,
        allowInvites: event.ticket.allowInvites ?? undefined,
        defaultInviteAllowance: event.ticket.defaultInviteAllowance ?? undefined,
        preCheckoutQuestion: event.ticket.preCheckoutQuestion ?? null
      },
      extras: event.extras
    }
  });
}

export async function createPlusOneInvite(ticketId: string, maxUses?: number): Promise<InviteRecord | null> {
  try {
    const convex = getConvex() as any;
    const payload = await convex.mutation('invites:createPlusOne', {
      ticketId,
      maxUses: typeof maxUses === 'number' ? maxUses : undefined
    });
    if (!payload) return null;
    return mapInviteRecord(payload);
  } catch (error) {
    console.error('Failed to create invite', error);
    return null;
  }
}

export async function regenerateInvite(code: string): Promise<InviteRecord | null> {
  try {
    const convex = getConvex() as any;
    const payload = await convex.mutation('invites:regenerate', { code });
    if (!payload) return null;
    return mapInviteRecord(payload);
  } catch (error) {
    console.error('Failed to regenerate invite', error);
    return null;
  }
}

export async function revokeInvite(code: string): Promise<InviteRecord | null> {
  try {
    const convex = getConvex() as any;
    const payload = await convex.mutation('invites:revoke', { code });
    if (!payload) return null;
    return mapInviteRecord(payload);
  } catch (error) {
    console.error('Failed to revoke invite', error);
    return null;
  }
}

/**
 * Redeem an invite (two-lists slice 06). Redemption grants standing, not a
 * ticket: it returns {eventId, standing:'invited', inviterName} — the guest-list
 * ticket is claimed later at purchase. No ticket is returned.
 */
export async function redeemInvite(args: { code: string; personId?: string; person?: PersonIdentityPayload }): Promise<{
  eventId: string;
  standing: string;
  inviterName: string | null;
  invite: InviteRecord;
} | null> {
  try {
    const convex = getConvex() as any;
    const payload = await convex.mutation('invites:redeem', {
      code: args.code,
      personId: args.personId,
      person: args.person
    });
    if (!payload || !payload.invite) return null;
    return {
      eventId: payload.eventId,
      standing: payload.standing ?? 'invited',
      inviterName: payload.inviterName ?? null,
      invite: mapInviteRecord(payload.invite)
    };
  } catch (error) {
    console.error('Failed to redeem invite', error);
    throw error instanceof Error ? error : new Error('Failed to redeem invite');
  }
}

export interface InvitePreview {
  eventId: string | null;
  inviterName: string | null;
  kind: string | null;
  valid: boolean;
}

/** Public, auth-free invite preview for the signed-out `?invite=` landing. */
export async function previewInvite(code: string): Promise<InvitePreview | null> {
  try {
    const convex = getConvex() as any;
    const payload = await convex.query('invites:preview', { code });
    if (!payload) return null;
    return {
      eventId: payload.eventId ?? null,
      inviterName: payload.inviterName ?? null,
      kind: payload.kind ?? null,
      valid: Boolean(payload.valid)
    };
  } catch (error) {
    console.error('Failed to preview invite', error);
    return null;
  }
}

export interface PersonalInvitePayload {
  eventId: string;
  targetPersonId?: string;
  targetTicketId?: string;
  targetName?: string;
  targetEmail?: string;
}

export async function createPersonalInvite(payload: PersonalInvitePayload): Promise<InviteRecord | null> {
  try {
    const convex = getConvex() as any;
    const result = await convex.mutation('invites:createPersonal', {
      eventId: payload.eventId,
      targetPersonId: payload.targetPersonId,
      targetTicketId: payload.targetTicketId,
      targetName: payload.targetName,
      targetEmail: payload.targetEmail
    });
    if (!result) return null;
    return mapInviteRecord(result);
  } catch (error) {
    console.error('Failed to create personal invite', error);
    return null;
  }
}

export async function bulkEnsurePersonalInvites(eventId: string): Promise<BulkPersonalInviteResult | null> {
  try {
    const convex = getConvex() as any;
    const result = await convex.mutation('invites:bulkEnsurePersonal', { eventId });
    if (!result) return null;
    return result as BulkPersonalInviteResult;
  } catch (error) {
    console.error('Failed to ensure personal invites', error);
    throw error instanceof Error ? error : new Error('Failed to ensure personal invites');
  }
}

export async function fetchPersonalInvitesExport(eventId: string): Promise<PersonalInviteExportRow[]> {
  try {
    const convex = getConvex() as any;
    const result = await convex.query('invites:listPersonalForExport', { eventId });
    if (!Array.isArray(result)) return [];
    return result as PersonalInviteExportRow[];
  } catch (error) {
    console.error('Failed to load personal invite export', error);
    throw error instanceof Error ? error : new Error('Failed to load invite export');
  }
}

export async function deleteInvite(code: string): Promise<boolean> {
  try {
    const convex = getConvex() as any;
    await convex.mutation('invites:remove', { code });
    return true;
  } catch (error) {
    console.error('Failed to delete invite', error);
    return false;
  }
}

export async function fetchAdminGuestlist(eventId: string): Promise<AdminGuestlistResponse | null> {
  try {
    const convex = getConvex() as any;
    const payload = await convex.query('admin:listGuestlist', { eventId });
    if (!payload) return null;
    return payload as AdminGuestlistResponse;
  } catch (error) {
    console.error('Failed to load Convex guestlist', error);
    return null;
  }
}

export async function fetchPeopleDirectory(): Promise<AdminPersonRecord[]> {
  try {
    const convex = getConvex() as any;
    const payload = await convex.query('admin:listPeople', {});
    if (!Array.isArray(payload)) {
      return [];
    }
    return payload as AdminPersonRecord[];
  } catch (error) {
    console.error('Failed to load Convex people directory', error);
    return [];
  }
}

/** Full Stripe refund of one checkout + platform-side cleanup. Managers only. */
export async function refundOrder(orderId: string): Promise<{
  ok: boolean;
  refundId: string;
  amount: number | null;
  currency: string | null;
  status: string | null;
}> {
  const convex = getConvex() as any;
  return convex.action('stripe:refundOrder', { orderId });
}

export async function fetchOrders(limit?: number, eventId?: string): Promise<AdminOrderRecord[]> {
  try {
    const convex = getConvex() as any;
    const args: Record<string, unknown> = {};
    if (typeof limit === 'number') args.limit = limit;
    if (eventId) args.eventId = eventId;
    const payload = await convex.query('admin:listOrders', args);
    if (!Array.isArray(payload)) {
      return [];
    }
    return payload as AdminOrderRecord[];
  } catch (error) {
    console.error('Failed to load Convex orders', error);
    return [];
  }
}

export async function setTicketRedeemed(ticketId: string, redeemed: boolean): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('tickets:setRedeemed', { ticketId, redeemed });
}

// The door fact — stamps/clears checkedInAt, independent of guest-list status.
export async function setTicketCheckedIn(ticketId: string, checkedIn: boolean): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('tickets:setCheckedIn', { ticketId, checkedIn });
}

export interface CommunityApiKeySummary {
  name: string;
  prefix: string;
  status: string;
  actsAs: string;
  createdAt: number;
  lastUsedAt: number | null;
}

export async function createCommunityApiKey(
  communityId: string,
  name: string
): Promise<{ secret: string; prefix: string }> {
  const convex = getConvex() as any;
  return await convex.mutation('apiKeys:createForCommunity', { communityId, name });
}

export async function fetchCommunityApiKeys(communityId: string): Promise<CommunityApiKeySummary[]> {
  const convex = getConvex() as any;
  const payload = await convex.query('apiKeys:listForCommunity', { communityId });
  return Array.isArray(payload) ? (payload as CommunityApiKeySummary[]) : [];
}

export async function revokeCommunityApiKey(communityId: string, prefix: string): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('apiKeys:revokeForCommunity', { communityId, prefix });
}

export interface PersonIdentityPayload {
  externalId?: string;
  name: string;
  email?: string;
  phone?: string;
  metadata?: unknown;
}

export interface ConvexPerson {
  _id: string;
  [key: string]: unknown;
}

export async function ensureConvexPerson(identity: PersonIdentityPayload): Promise<ConvexPerson | null> {
  try {
    const convex = getConvex() as any;
    const record = await convex.mutation('persons:getOrCreate', { identity });
    if (!record) return null;
    const id = typeof record._id === 'string' ? record._id : String(record._id);
    return { ...record, _id: id };
  } catch (error) {
    console.error('Failed to ensure Convex person', error);
    return null;
  }
}

export async function confirmPersonName(
  name: string,
  marketingConsent?: boolean
): Promise<ConvexPerson | null> {
  const convex = getConvex() as any;
  const record = await convex.mutation('persons:confirmName', {
    name,
    marketingConsent: marketingConsent ?? undefined
  });
  if (!record) return null;
  const id = typeof record._id === 'string' ? record._id : String(record._id);
  return { ...record, _id: id };
}

export async function recordMarketingConsent(
  status: 'granted' | 'declined',
  source: 'signup' | 'checkout' | 'settings'
): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('persons:recordConsent', { status, source });
}

export async function setPreCheckoutAnswer(ticketId: string, answer: string): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('tickets:setPreCheckoutAnswer', {
    ticketId,
    answer
  });
}

export async function fetchTicketsForPerson(personId: string): Promise<UserTicketSummary[]> {
  try {
    const convex = getConvex() as any;
    const tickets = await convex.query('tickets:listByPerson', { personId });
    if (!Array.isArray(tickets)) {
      return [];
    }
    return tickets.map(ticket => ({
      id: typeof ticket._id === 'string' ? ticket._id : String(ticket._id),
      code: ticket.code,
      eventId: ticket.eventId,
      person: mapPersonSummary(ticket.person, ticket.person ? undefined : personId),
      redeemed: ticket.status === 'redeemed',
      redeemedAt: ticket.redeemedAt ?? null,
      extras: mapTicketExtras(ticket.extras ?? []),
      status: ticket.status
    }));
  } catch (error) {
    console.error('Failed to load Convex tickets for person', error);
    return [];
  }
}

export interface AdminTicketRecord {
  docId: any;
  code: string;
  eventId: string;
  redeemed: boolean;
  redeemedAt: string | null;
  extras: TicketExtraPurchase[];
  person: PersonSummary | null;
}

function mapAdminTicket(ticket: any): AdminTicketRecord {
  return {
    docId: ticket._id,
    code: ticket.code,
    eventId: ticket.eventId,
    redeemed: ticket.status === 'redeemed',
    redeemedAt: ticket.redeemedAt ?? null,
    extras: mapTicketExtras(ticket.extras ?? []),
    person: ticket.person ? mapPersonSummary(ticket.person, ticket.personId ? String(ticket.personId) : undefined) : null
  };
}

export async function fetchAdminTickets(eventId?: string): Promise<AdminTicketRecord[]> {
  try {
    const convex = getConvex() as any;
    const args = eventId ? { eventId } : {};
    const tickets = await convex.query('tickets:listAdmin', args);
    if (!Array.isArray(tickets)) {
      return [];
    }
    return tickets.map(mapAdminTicket);
  } catch (error) {
    console.error('Failed to load Convex admin tickets', error);
    return [];
  }
}

export interface AdminTicketUpsertInput {
  ticketId?: any;
  eventId: string;
  code?: string;
  personId?: string;
  person?: { name: string; email?: string; phone?: string };
  redeemed: boolean;
}

export async function upsertAdminTicket(input: AdminTicketUpsertInput): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('tickets:upsert', {
    ticketId: input.ticketId ?? undefined,
    eventId: input.eventId,
    code: input.code,
    personId: input.personId ?? undefined,
    person: input.person,
    redeemed: input.redeemed
  });
}

export async function deleteAdminTicket(ticketId: any): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('tickets:deleteTicket', { ticketId });
}

export async function bulkDeleteAdminTickets(ticketIds: any[]): Promise<void> {
  if (ticketIds.length === 0) return;
  const convex = getConvex() as any;
  await convex.mutation('admin:bulkDelete', { ticketIds });
}

export async function bulkUpdateAdminTickets(ticketIds: any[], redeemed: boolean): Promise<void> {
  if (ticketIds.length === 0) return;
  const convex = getConvex() as any;
  await convex.mutation('admin:bulkUpdate', {
    ticketIds,
    updates: { redeemed }
  });
}

export async function importAdminTickets(eventId: string, names: string[]): Promise<void> {
  if (names.length === 0) return;
  const convex = getConvex() as any;
  await convex.mutation('admin:importTickets', {
    eventId,
    tickets: names.map(name => ({ person: { name } }))
  });
}

export async function deleteAdminEvent(eventId: string): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('events:deleteEvent', { eventId });
}

export interface AdminImportTicketPayload {
  eventId: string;
  tickets: Array<{ person: { name: string; email?: string; phone?: string }; code?: string }>;
}

export async function importOrdersFromCsv(eventId: string, rows: Array<{ name: string; email?: string; phone?: string; code?: string }>): Promise<void> {
  if (rows.length === 0) return;
  const convex = getConvex() as any;
  await convex.mutation('admin:importTickets', {
    eventId,
    tickets: rows.map(row => ({
      code: row.code,
      person: {
        name: row.name,
        email: row.email,
        phone: row.phone
      }
    }))
  });
}

export async function fetchAdminInventory(eventId: string | null): Promise<AdminInventorySkuRecord[]> {
  if (!eventId) return [];
  const convex = getConvex() as any;
  const result = await convex.query('admin:listInventory', { eventId });
  if (!Array.isArray(result)) return [];
  return result as AdminInventorySkuRecord[];
}

export async function updateAdminExtraCapacity(eventId: string, sku: string, capacity: number, reason?: string): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('admin:updateExtraCapacity', {
    eventId,
    sku,
    capacity,
    reason: reason ?? undefined
  });
}

export async function fetchAdminEventTickets(eventId: string): Promise<AdminEventTicketSummary[]> {
  const convex = getConvex() as any;
  const result = await convex.query('admin:listTicketsByEvent', { eventId });
  if (!Array.isArray(result)) return [];
  return result as AdminEventTicketSummary[];
}

export async function updateAdminPerson(personId: string, payload: { name: string; email?: string | null; phone?: string | null }): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('persons:update', {
    personId,
    name: payload.name,
    email: payload.email ?? undefined,
    phone: payload.phone ?? undefined
  });
}

export async function deleteAdminPerson(personId: string): Promise<void> {
  const convex = getConvex() as any;
  await convex.mutation('persons:remove', { personId });
}

function mapHouseQuestionnaire(doc: any): HouseQuestionnaireSubmission {
  return {
    id: typeof doc._id === 'string' ? doc._id : String(doc._id ?? ''),
    userId: doc.userId ?? '',
    email: doc.email ?? '',
    answers: (doc.answers ?? {}) as HouseQuestionnaireAnswers,
    submittedAt: doc.submittedAt ?? Date.now(),
    finalized: doc.finalized === true
  };
}

export async function fetchMyHouseQuestionnaire(): Promise<HouseQuestionnaireSubmission | null> {
  try {
    const convex = getConvex() as any;
    const result = await convex.query('houseQuestionnaires:mine', {});
    if (!result) return null;
    return mapHouseQuestionnaire(result);
  } catch (error) {
    console.error('Failed to fetch house questionnaire', error);
    return null;
  }
}

export async function submitHouseQuestionnaire(
  answers: HouseQuestionnaireAnswers,
  finalized = false
): Promise<HouseQuestionnaireSubmission | null> {
  const convex = getConvex() as any;
  try {
    const result = await convex.mutation('houseQuestionnaires:submit', { answers, finalized });
    if (!result) return null;
    return mapHouseQuestionnaire(result);
  } catch (error) {
    console.error('Failed to submit house questionnaire', error);
    throw error;
  }
}

// --- Communities -------------------------------------------------------------

export async function joinCommunity(
  slug: string,
  note?: string | null,
  answers?: Record<string, unknown> | null
): Promise<{ status: string }> {
  const convex = getConvex() as any;
  const payload: Record<string, unknown> = { slug, note: note ?? null };
  if (answers) payload.answers = answers;
  return convex.mutation('communities:join', payload);
}

export async function leaveCommunity(slug: string): Promise<{ left: boolean }> {
  const convex = getConvex() as any;
  return convex.mutation('communities:leave', { slug });
}

export async function createCommunity(payload: {
  name: string;
  description?: string | null;
  visibility: 'public' | 'apply' | 'hidden';
}): Promise<{ slug: string }> {
  const convex = getConvex() as any;
  return convex.mutation('communities:create', payload);
}

export async function decideCommunityApplication(
  membershipId: string,
  approve: boolean
): Promise<{ status: string }> {
  const convex = getConvex() as any;
  return convex.mutation('communities:decideApplication', { membershipId, approve });
}

export async function setMembershipWelcomeSent(
  membershipId: string,
  sent: boolean
): Promise<{ welcomeSentAt: number | null }> {
  const convex = getConvex() as any;
  return convex.mutation('communities:setWelcomeSent', { membershipId, sent });
}

export async function setCommunityMemberRole(
  membershipId: string,
  role: 'admin' | 'member'
): Promise<{ role: string }> {
  const convex = getConvex() as any;
  return convex.mutation('communities:setMemberRole', { membershipId, role });
}

export async function removeCommunityMember(membershipId: string): Promise<{ removed: boolean }> {
  const convex = getConvex() as any;
  return convex.mutation('communities:removeMember', { membershipId });
}

export async function updateCommunity(payload: {
  communityId: string;
  name?: string;
  description?: string | null;
  visibility?: 'public' | 'apply' | 'hidden';
  applicationMode?: 'note' | 'questionnaire';
  defaultAllowInvites?: boolean | null;
  defaultInviteAllowance?: number | null;
  themeColor?: string | null;
  logoUrl?: string | null;
  welcomeMessage?: string | null;
}): Promise<{ ok: boolean }> {
  const convex = getConvex() as any;
  return convex.mutation('communities:update', payload);
}

// --- Questionnaires -----------------------------------------------------------

/** Upload one image-question answer: mint an upload URL, POST the file, and
 * return the storage id to use as the answer value. Signed-in only. */
export async function uploadQuestionnaireImage(file: File): Promise<string> {
  const convex = getConvex() as any;
  const uploadUrl: string = await convex.mutation('questionnaires:generateUploadUrl', {});
  const response = await fetch(uploadUrl, {
    method: 'POST',
    headers: { 'Content-Type': file.type },
    body: file
  });
  if (!response.ok) {
    throw new Error('Upload failed — try again.');
  }
  const { storageId } = await response.json();
  if (typeof storageId !== 'string' || !storageId) {
    throw new Error('Upload failed — try again.');
  }
  return storageId;
}

export async function fetchQuestionnaireForApplicant(
  slug: string
): Promise<{ questions: QuestionDefinition[]; version: number } | null> {
  const convex = getConvex() as any;
  return convex.query('questionnaires:forApplicant', { slug });
}

export async function fetchQuestionnaire(
  communityId: string
): Promise<{ questions: QuestionDefinition[]; version: number; updatedAt: number } | null> {
  const convex = getConvex() as any;
  return convex.query('questionnaires:get', { communityId });
}

export async function saveQuestionnaire(
  communityId: string,
  questions: QuestionDefinition[]
): Promise<{ version: number }> {
  const convex = getConvex() as any;
  return convex.mutation('questionnaires:save', { communityId, questions });
}

// --- Event access, applications ------------------------------------------------

export async function setEventTicketing(payload: {
  eventId: string;
  capacity?: number | null;
  tiers?: TicketTier[] | null;
}): Promise<{ ok: boolean }> {
  const convex = getConvex() as any;
  return convex.mutation('events:setTicketing', payload);
}

export async function setEventAccess(payload: {
  eventId: string;
  visibilityOverride?: 'inherit' | 'public' | 'unlisted';
  ticketAccess?: 'invite' | 'members' | 'open' | 'apply';
  applicationMode?: 'inherit' | 'note' | 'questionnaire';
}): Promise<{ ok: boolean }> {
  const convex = getConvex() as any;
  return convex.mutation('events:setAccess', payload);
}

export async function applyToEvent(
  eventId: string,
  note?: string | null,
  answers?: Record<string, unknown> | null
): Promise<{ status: string }> {
  const convex = getConvex() as any;
  const payload: Record<string, unknown> = { eventId, note: note ?? null };
  if (answers) payload.answers = answers;
  return convex.mutation('eventApplications:apply', payload);
}

export async function decideEventApplication(
  applicationId: string,
  approve: boolean
): Promise<{ status: string; ticketCode?: string }> {
  const convex = getConvex() as any;
  return convex.mutation('eventApplications:decide', { applicationId, approve });
}

export async function setTicketInviteAllowance(
  ticketId: string,
  allowance: number | null
): Promise<{ inviteAllowance: number | null }> {
  const convex = getConvex() as any;
  return convex.mutation('tickets:setInviteAllowance', { ticketId, allowance });
}

// --- Shared drawing canvases ("Play vidamor") -----------------------------------

export interface CanvasStroke {
  id: string;
  color: string;
  size: number;
  points: number[];
  durationMs?: number;
  createdAt: number;
}

export async function addCanvasStroke(payload: {
  canvasKey: string;
  color: string;
  size: number;
  points: number[];
  durationMs?: number;
}): Promise<{ id: string }> {
  const convex = getConvex() as any;
  return convex.mutation('canvasStrokes:add', payload);
}

/** One-shot history load (newest slice, oldest-first for layering). */
export async function fetchCanvasStrokes(canvasKey: string): Promise<CanvasStroke[]> {
  const convex = getConvex() as any;
  return convex.query('canvasStrokes:list', { canvasKey });
}
